import { useState } from 'react';
import { REGIONS, STATE_GROUPS, REGION_CONTENT, REGION_BY_ID, TOTAL_BY_REGION, formatUSD } from './revenueData';
import type { RegionContent } from './revenueData';

type ContentKey = 'tournaments' | 'news' | 'sponsors' | 'bulletins';

const SECTIONS: { key: ContentKey; icon: string; title: string; blurb: string }[] = [
  { key: 'tournaments', icon: 'ri-trophy-line', title: 'Tournament listings', blurb: 'The calendar shows only the tournaments in the selected market.' },
  { key: 'news', icon: 'ri-newspaper-line', title: 'News content', blurb: 'Stories are tagged by region so readers see local coverage first.' },
  { key: 'sponsors', icon: 'ri-megaphone-line', title: 'Sponsorship placements', blurb: 'Advertisers buy placement in the markets they care about.' },
  { key: 'bulletins', icon: 'ri-pushpin-2-line', title: 'Community bulletins', blurb: 'Notices are grouped by region on the community board.' },
];

const ALL: RegionContent = {
  tournaments: REGIONS.flatMap((r) => REGION_CONTENT[r.id].tournaments).slice(0, 6),
  news: REGIONS.flatMap((r) => REGION_CONTENT[r.id].news).slice(0, 6),
  sponsors: Array.from(new Set(REGIONS.flatMap((r) => REGION_CONTENT[r.id].sponsors))),
  bulletins: REGIONS.flatMap((r) => REGION_CONTENT[r.id].bulletins).slice(0, 6),
  newsletter: 'All regional editions — one master send, split by market',
};

export default function RegionExplorer() {
  const [selected, setSelected] = useState<string>('all');

  const content: RegionContent = selected === 'all' ? ALL : REGION_CONTENT[selected];
  const selectedRegion = selected === 'all' ? null : REGION_BY_ID[selected];

  return (
    <div className="space-y-6">
      {/* Region selector */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold text-2xl text-foreground-950">Choose a region</h3>
            <p className="mt-1 text-sm text-foreground-600">One platform, many markets — visitors pick where they are, or browse everything.</p>
          </div>
          <button
            type="button"
            onClick={() => setSelected('all')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border font-label font-bold uppercase tracking-wider text-[11px] whitespace-nowrap cursor-pointer transition-colors ${selected === 'all' ? 'bg-foreground-950 border-foreground-950 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-foreground-700'}`}
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-global-line"></i></span>
            View all regions
          </button>
        </div>

        <div className="mt-4 space-y-4">
          {STATE_GROUPS.map((group) => (
            <div key={group.state}>
              <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-2">{group.state}</p>
              <div className="flex flex-wrap gap-2.5">
                {group.regions.map((r) => {
                  const on = selected === r.id;
                  return (
                    <button key={r.id} type="button" onClick={() => setSelected(r.id)} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border font-label font-semibold text-xs whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
                      {r.city}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Region summary strip */}
      <div className="rounded-lg border border-background-200 bg-background-100 p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Now viewing</p>
          <p className="font-heading font-bold text-2xl text-foreground-950">{selectedRegion ? `${selectedRegion.city}, ${selectedRegion.state}` : 'All regions'}</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div>
            <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Regions</p>
            <p className="font-heading font-extrabold text-xl text-foreground-950">{selectedRegion ? '1' : REGIONS.length}</p>
          </div>
          <div>
            <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Sample monthly revenue</p>
            <p className="font-heading font-extrabold text-xl text-foreground-950">{selectedRegion ? formatUSD(TOTAL_BY_REGION[selectedRegion.id]) : formatUSD(Object.values(TOTAL_BY_REGION).reduce((a, b) => a + b, 0))}</p>
          </div>
        </div>
      </div>

      {/* Content panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {SECTIONS.map((s) => (
          <div key={s.key} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-xl"><i className={s.icon}></i></span>
              <h4 className="font-heading font-bold text-xl text-foreground-950">{s.title}</h4>
            </div>
            <p className="mt-2 text-xs text-foreground-500">{s.blurb}</p>
            <ul className="mt-4 space-y-2.5">
              {content[s.key].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <span className="w-4 h-4 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-arrow-right-s-line"></i></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Newsletter preferences */}
        <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-md bg-secondary-500 text-foreground-950 flex items-center justify-center text-xl"><i className="ri-mail-send-line"></i></span>
            <h4 className="font-heading font-bold text-xl text-foreground-950">Newsletter preferences</h4>
          </div>
          <p className="mt-2 text-xs text-foreground-500">Subscribers choose regional editions; one master send is split per market.</p>
          <p className="mt-4 font-heading font-bold text-lg text-foreground-950">{content.newsletter}</p>
          {selectedRegion && (
            <p className="mt-3 text-xs text-secondary-900">Readers in {selectedRegion.city} would receive their local digest by default, and could opt into other markets.</p>
          )}
        </div>
      </div>
    </div>
  );
}